import React from "react";

export const StatusBadge = ({ status }) => {
  const getStyles = (s) => {
    const normalize = s.toLowerCase();
    if (normalize.includes("process") || normalize.includes("inward"))
      return "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20";
    if (normalize.includes("available"))
      return "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20";
    if (normalize.includes("memo"))
      return "bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-600/20";
    if (normalize.includes("sold"))
      return "bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-500/20";
    return "bg-slate-50 text-slate-700 ring-1 ring-inset ring-slate-500/10";
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-medium tracking-wide shadow-sm ${getStyles(
        status
      )}`}
    >
      {status}
    </span>
  );
};

export const Button = ({
  children,
  variant = "primary",
  icon: Icon,
  size = "md",
  className = "",
  ...props
}) => {
  const baseStyles =
    "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]";

  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-base",
  };

  const variants = {
    primary:
      "bg-slate-900 text-white hover:bg-slate-800 shadow-md shadow-slate-900/10 hover:shadow-lg border border-transparent",
    secondary:
      "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 shadow-sm",
    ghost: "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
    glass:
      "bg-white/60 backdrop-blur-md border border-white/50 text-slate-800 hover:bg-white/80 shadow-sm ring-1 ring-slate-900/5",
    danger: "bg-red-50 text-red-600 border border-red-100 hover:bg-red-100",
  };

  return (
    <button
      className={`${baseStyles} ${sizes[size]} ${variants[variant]} ${className}`}
      {...props}
    >
      {Icon && <Icon size={size === "sm" ? 14 : 16} strokeWidth={2} />}
      {children}
    </button>
  );
};

export const Card = ({ children, className = "" }) => (
  <div
    className={`bg-white rounded-xl border border-slate-200/60 shadow-sm ring-1 ring-slate-900/5 ${className}`}
  >
    {children}
  </div>
);

export const StatCard = ({ label, value, subtext, trend }) => (
  <Card className="p-4 min-w-[160px] bg-white/80 backdrop-blur-sm">
    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
      {label}
    </p>
    <div className="flex items-baseline gap-2">
      <div className="text-2xl font-bold text-slate-900 tabular-nums tracking-tight">
        {value}
      </div>
      {trend && (
        <span
          className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
            trend === "up"
              ? "bg-emerald-100 text-emerald-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {trend === "up" ? "↑" : "↓"} 2.4%
        </span>
      )}
    </div>
    {subtext && (
      <div className="text-xs text-slate-400 mt-1 font-medium">{subtext}</div>
    )}
  </Card>
);

export const SearchInput = ({ placeholder, onChange }) => (
  <div className="relative group w-full">
    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
      <svg
        className="h-4 w-4 text-slate-400 group-focus-within:text-brand-600 transition-colors"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
    </div>
    <input
      type="text"
      className="block w-full pl-10 pr-3 py-2.5 border border-slate-200/80 rounded-lg bg-slate-50/50 hover:bg-white focus:bg-white backdrop-blur-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/10 focus:border-brand-500 transition-all text-sm shadow-sm"
      placeholder={placeholder}
      onChange={(e) => onChange && onChange(e.target.value)}
    />
  </div>
);
